import React, { useContext } from "react";
import { graphql, useFragment, usePaginationFragment } from "react-relay";
import { Link } from "react-router-dom";

import {
	faAlignLeft,
	faArrowUp,
	faBullseyeArrow,
	faCheck,
	faCheckCircle,
	faCheckSquare,
	faClock,
	faCommentAltLines,
	faEllipsisV,
	faFile,
	faFileImport,
	faFileMinus,
	faFilePlus,
	faHandPointRight,
	faTheaterMasks,
	faThumbsUp,
	faVideo,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faCaretDown, faCaretUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { faSparklingBriefcase } from "@awesome.me/kit-4c83d50d0d/icons/kit/custom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Sentry from "@sentry/browser";
import cx from "classnames";
import { debounce } from "lodash";
import moment from "moment";
import { fetchQuery, readInlineData } from "relay-runtime";

import AppContext from "~/common/AppContext";
import LoadMoreButton from "~/common/LoadMoreButton";
import { ScrollContext } from "~/layout/ScrollContext";
import { useDailyUpdate } from "~/lib/dailyUpdate";
import { OuterObjectContext } from "~/lib/outerObjectContext";
import { commitMutationPromise, useLazyLoadQuery } from "~/lib/relayUtil";
import { withFallbacks } from "~/lib/suspense";
import WebsocketClient from "~/lib/websocket";
import { environment } from "~/models";
import { Button, LoadingSpinner } from "~/ui";
import IconButton from "~/ui/legacy/IconButton";
import { Menu } from "~/ui/menu";
import toast from "~/ui/toast";

import type { NotificationCenter_List$key } from "~/__generated__/NotificationCenter_List.graphql";
import type { NotificationCenter_MarkAllAsReadMutation as MarkAllAsReadMutationType } from "~/__generated__/NotificationCenter_MarkAllAsReadMutation.graphql";
import type { NotificationCenter_MarkAsReadMutation as MarkAsReadMutationType } from "~/__generated__/NotificationCenter_MarkAsReadMutation.graphql";
import type { NotificationCenter_Notification$key } from "~/__generated__/NotificationCenter_Notification.graphql";
import type { NotificationCenter_SetFiltersMutation as SetFiltersMutationType } from "~/__generated__/NotificationCenter_SetFiltersMutation.graphql";
import type { NotificationCenter_squashEditedNoteNotificationsNotification$key as squashEditedNoteNotificationsNotification$key } from "~/__generated__/NotificationCenter_squashEditedNoteNotificationsNotification.graphql";
import type {
	NotificationCenterQuery,
	NotificationFilterChoice,
} from "~/__generated__/NotificationCenterQuery.graphql";

import styles from "./NotificationCenter.module.css";

const MarkAsReadMutation = graphql`
	mutation NotificationCenter_MarkAsReadMutation($notificationId: ID!) {
		NotificationCenter_MarkAsRead(notificationId: $notificationId) {
			notification {
				id
				unread
			}

			notificationCenter {
				id
				unreadNotificationCount
				otherNotificationCount

				...NotificationCenter_List
			}
		}
	}
`;

const setNotificationFiltersMutation = graphql`
	mutation NotificationCenter_SetFiltersMutation($enabledFilters: [NotificationFilterChoice!]!) {
		NotificationCenter_SetFilters(enabledFilters: $enabledFilters) {
			notificationCenter {
				id
				unreadNotificationCount
				otherNotificationCount
				notificationPanelFilters {
					key
					value
				}

				...NotificationCenter_List
			}
		}
	}
`;

async function markNotificationAsRead(notificationId: string): Promise<boolean> {
	try {
		const result = await commitMutationPromise<MarkAsReadMutationType>(environment, {
			mutation: MarkAsReadMutation,
			variables: { notificationId },
		});
		return !!result.NotificationCenter_MarkAsRead;
	} catch (e) {
		Sentry.captureException(e);
		return false;
	}
}

const MarkAllAsReadMutation = graphql`
	mutation NotificationCenter_MarkAllAsReadMutation {
		NotificationCenter_MarkAllAsRead {
			notificationCenter {
				id
				unreadNotificationCount
				otherNotificationCount

				...NotificationCenter_List
			}
		}
	}
`;

async function markAllNotificationsAsRead(): Promise<boolean> {
	try {
		const result = await commitMutationPromise<MarkAllAsReadMutationType>(environment, {
			mutation: MarkAllAsReadMutation,
			variables: {},
		});
		return !!result.NotificationCenter_MarkAllAsRead?.notificationCenter;
	} catch (e) {
		Sentry.captureException(e);
		return false;
	}
}

async function setNotificationsFilters(enabledFilters: NotificationFilterChoice[]): Promise<boolean> {
	try {
		const result = await commitMutationPromise<SetFiltersMutationType>(environment, {
			mutation: setNotificationFiltersMutation,
			variables: { enabledFilters },
		});
		return !!result.NotificationCenter_SetFilters?.notificationCenter;
	} catch (e) {
		Sentry.captureException(e);
		return false;
	}
}

function useUpdateEveryMinute() {
	const [, forceUpdate] = React.useState({});

	React.useEffect(() => {
		setTimeout(function doForceUpdate() {
			forceUpdate({});
			setTimeout(doForceUpdate, 60_000);
		}, 60_000);
	}, []);
}

type NotificationIconProps = {
	verb: string;
};

function NotificationIcon({ verb }: NotificationIconProps) {
	let icon: React.ReactNode = <FontAwesomeIcon icon={faFile} />;

	if (verb == "EDITED_NOTE") {
		icon = <FontAwesomeIcon icon={faFile} />;
	} else if (verb == "COMPLETED_ACTION_ITEM") {
		icon = <FontAwesomeIcon icon={faCheck} />;
	} else if (verb == "ASSIGNED_ACTION_ITEM") {
		icon = <FontAwesomeIcon icon={faCheckSquare} />;
	} else if (verb == "ASSIGNED_TALKING_POINT") {
		icon = <FontAwesomeIcon icon={faCheckCircle} />;
	} else if (verb == "ADDED_DUE_DATE" || verb == "EDITED_DUE_DATE") {
		icon = <FontAwesomeIcon icon={faClock} />;
	} else if (verb == "SHARED_STREAM") {
		icon = <FontAwesomeIcon icon={faFilePlus} />;
	} else if (verb == "UNSHARED_STREAM" || verb == "USER_LEFT_SHARED_STREAM") {
		icon = <FontAwesomeIcon icon={faFileMinus} />;
	} else if (verb == "MERGED_STREAMS") {
		icon = <FontAwesomeIcon icon={faFileImport} />;
	} else if (verb == "EDITED_STREAM_DESCRIPTION") {
		icon = <FontAwesomeIcon icon={faAlignLeft} />;
	} else if (verb == "REMINDED_FEEDBACK_RESPONSE") {
		icon = <FontAwesomeIcon icon={faHandPointRight} />;
	} else if (
		[
			"REQUESTED_FEEDBACK",
			"RESPONDED_TO_FEEDBACK_REQUEST",
			"GAVE_FEEDBACK",
			"GAVE_MEETING_FEEDBACK",
			"SHARED_FEEDBACK",
		].includes(verb)
	) {
		icon = <FontAwesomeIcon icon={faThumbsUp} />;
	} else if (verb.startsWith("COMMENT")) {
		icon = <FontAwesomeIcon icon={faCommentAltLines} />;
	} else if (verb.startsWith("USER_DELEGATION")) {
		icon = <FontAwesomeIcon icon={faTheaterMasks} />;
	} else if (verb.startsWith("OBJECTIVE_") || verb.startsWith("KEY_RESULT_")) {
		icon = <FontAwesomeIcon icon={faBullseyeArrow} />;
	} else if (verb === "MEETING_BRIEF_READY") {
		icon = <FontAwesomeIcon icon={faSparklingBriefcase} />;
	} else if (verb === "RECORDING_FIRST_VIEWED") {
		icon = <FontAwesomeIcon icon={faVideo} />;
	}

	return <div className={styles.notificationIconRead}>{icon}</div>;
}

// unread notifications are rendered in a span instead of an icon, as with resizing and moving the icon,
// it would occasionally bleed due to resolution, and not look entirely circular.
function UnreadDot() {
	return (
		<span className={styles.notificationIconUnread}>
			<span className={styles.unreadDot} />
		</span>
	);
}

function NotificationCenterEntry({
	notification: notificationKey,
	squashInfo,
}: {
	notification: NotificationCenter_Notification$key;
	squashInfo: SquashInfo | undefined;
}) {
	// Since we show the relative time of this notification, we need to update
	// that periodically or it will become out of date.
	useUpdateEveryMinute();

	const notification = useFragment(
		graphql`
			fragment NotificationCenter_Notification on NotificationCenterEntry {
				id
				unread
				createdAt
				title
				subtitle
				body
				url
				verb

				outerObject {
					... on Account {
						id
						title: name
					}

					... on Note {
						id
						title
						eventTitle
					}

					... on NoteStream {
						id
						title: displayName
					}

					... on FeedbackNode {
						id
						title
					}

					... on CommentThread {
						id
						title
					}

					... on Objective {
						id
						title
					}
				}
			}
		`,
		notificationKey,
	);

	const outerObjectName = notification.outerObject?.title ?? notification.outerObject?.eventTitle ?? "";

	const markAsRead = React.useCallback(async () => {
		// No point marking it as read if it's been read.
		if (!notification.unread) return;

		if (!(await markNotificationAsRead(notification.id))) {
			Sentry.captureMessage("Failed to mark notification as read");
		}
	}, [notification]);

	// If the title is an empty string it means that formatting likely failed on
	// the back end. We'll just exclude it from the list.
	if (!notification.title) return null;

	// EDITED_NOTE notifications are squashed; only the most recent one shows up,
	// and we use the oldest URL of the squashed notifications.
	let { unread, url } = notification;
	if (squashInfo) {
		let info;
		if (!(info = squashInfo.get(notification.id))) return null;
		({ oldestUrl: url, unread } = info);
	}

	// note edit notifications will be condensed into a notification without a distinct body being displayed.
	let notificationTitle: string = notification.title;
	let showNotificationBody = true;
	if (notification.verb == "EDITED_NOTE" && notification.body != null) {
		notificationTitle = notification.body;
		showNotificationBody = false;
	}

	return (
		<li className={styles.notificationContainer}>
			<Link className={cx(styles.notification, { [styles.unread]: notification.unread })} to={url} onClick={markAsRead}>
				<div className={styles.notificationTitleContainer}>
					<p className={styles.notificationText}>
						{unread ? <UnreadDot /> : <NotificationIcon verb={notification.verb} />}
					</p>
					<p className={styles.notificationText} title={notificationTitle}>
						{notificationTitle}
					</p>
				</div>
				{showNotificationBody && notification.body && <div className={styles.contextWrapper}>{notification.body}</div>}
				{/* FIXME: don't use moment? */}
				<p className={styles.notificationDetail}>
					{moment(notification.createdAt).fromNow()}
					{(notification.subtitle || outerObjectName) && (
						<>
							{" "}
							&mdash;{" "}
							<span title={notification.subtitle || outerObjectName}>{notification.subtitle || outerObjectName}</span>
						</>
					)}
				</p>
			</Link>
		</li>
	);
}

function useRefetchNotificationCenterOnNewNotification(userId: string, onRefetch?: () => void) {
	const outerObjectContext = React.useContext(OuterObjectContext);

	React.useEffect(() => {
		const socket = WebsocketClient.getInstance().getOrJoinGroup(`activities.${userId}`.replace(/=/g, ""), { userId });

		async function onSocketMessage(message: { data?: { notify?: boolean; collapseKey?: string } }) {
			if (!message.data?.notify) return;

			// Mark the new notification as read if we're viewing the thing it's
			// related to already.
			if (message.data?.collapseKey && outerObjectContext.isCleared(message.data.collapseKey)) {
				await outerObjectContext.markRelatedNotificationsAsRead();
			}

			onRefetch?.();
		}

		socket.on("message", onSocketMessage);

		return () => {
			socket.off("message", onSocketMessage);
			socket.close();
		};
	}, [userId, onRefetch, outerObjectContext]);
}

export function RefetchUnreadCountOnNewNotification({ userId }: { userId: string }) {
	useRefetchNotificationCenterOnNewNotification(
		userId,
		React.useCallback(() => {
			fetchQuery(
				environment,
				graphql`
					query NotificationCenter_RefetchUnreadCountOnNewNotificationQuery {
						viewer {
							notificationCenter {
								id
								unreadNotificationCount
								otherNotificationCount
							}
						}
					}
				`,
				{},
			).toPromise();
		}, []),
	);

	return null;
}

function EmptyState() {
	return <p className={styles.emptyState}>You have no notifications</p>;
}

// keyed by ID of latest notification in window
type SquashInfo = Map<string, { oldestUrl: string; unread: boolean }>;

const EDITED_NOTE_SQUASH_WINDOW = 4 * 60 * 60 * 1000; // 4 hours

function squashEditedNoteNotifications(notifications: readonly squashEditedNoteNotificationsNotification$key[]): {
	[outerObjectId: string]: SquashInfo;
} {
	const result: { [outerObjectId: string]: SquashInfo } = {};
	const windows: { [outerObjectId: string]: { end: number; latestId: string } } = {};

	for (const notificationKey of notifications) {
		const notification = readInlineData(
			graphql`
				fragment NotificationCenter_squashEditedNoteNotificationsNotification on NotificationCenterEntry @inline {
					id
					verb
					unread
					url
					createdAt

					outerObject {
						id
					}
				}
			`,
			notificationKey,
		);

		if (notification.verb !== "EDITED_NOTE" || !notification.outerObject) continue;

		const notificationTime = new Date(notification.createdAt).getTime();
		let window = windows[notification.outerObject.id];

		if (!window || notificationTime < window.end) {
			window = windows[notification.outerObject.id] = {
				latestId: notification.id,
				end: notificationTime - EDITED_NOTE_SQUASH_WINDOW,
			};
		}

		if (!(notification.outerObject.id in result)) {
			result[notification.outerObject.id] = new Map();
		}

		let squashInfo,
			{ unread } = notification;
		if ((squashInfo = result[notification.outerObject.id].get(window.latestId))) {
			unread ||= squashInfo.unread;
		}
		result[notification.outerObject.id].set(window.latestId, { oldestUrl: notification.url, unread });
	}

	return result;
}

const notificationPanelLabels: { [key in NotificationFilterChoice]: string } = {
	NOTES_EDITED: "Notes edited",
	RECORDING_ADDED: "Recordings added",
	ACTION_ITEMS: "Action items",
	TALKING_POINTS: "Talking points",
	STREAM: "Series activity",
	COMMENTS: "Comments & mentions",
	FEEDBACK: "Feedback",
	OBJECTIVES: "Objectives",
	USER_DELEGATION: "User delegation",
};

const guestOnlyNotificationTypes: NotificationFilterChoice[] = [
	"NOTES_EDITED",
	"RECORDING_ADDED",
	"ACTION_ITEMS",
	"TALKING_POINTS",
	"STREAM",
	"COMMENTS",
];

function NotificationCenterList({
	notificationCenter: viewerKey,
	userId,
}: {
	notificationCenter: NotificationCenter_List$key;
	userId: string;
}) {
	const { data, hasNext, loadNext, loadPrevious } = usePaginationFragment(
		graphql`
			fragment NotificationCenter_List on NotificationCenter
			@argumentDefinitions(
				first: { type: "Int", defaultValue: 20 }
				after: { type: "String" }
				last: { type: "Int" }
				before: { type: "String" }
			)
			@refetchable(queryName: "NotificationCenterPaginationQuery") {
				notifications(first: $first, after: $after, last: $last, before: $before)
					@connection(key: "NotificationCenter_notifications") {
					edges {
						cursorBefore
						node {
							id
							verb

							outerObject {
								id
							}

							...NotificationCenter_Notification
							...NotificationCenter_squashEditedNoteNotificationsNotification
						}
					}
				}
			}
		`,
		viewerKey,
	);

	const [showNewNotificationsButton, setShowNewNotificationsButton] = React.useState(false);

	const scrollTopRef = React.useRef(0);
	const onScroll = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {
		scrollTopRef.current = e.currentTarget.scrollTop;

		if (scrollTopRef.current === 0) setShowNewNotificationsButton(false);
	}, []);

	const onNewNotificationsButtonClick = React.useCallback(() => {
		setShowNewNotificationsButton(false);
		scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
	}, []);

	useRefetchNotificationCenterOnNewNotification(
		userId,
		React.useCallback(() => {
			loadPrevious(10, {
				onComplete(error) {
					if (!error && scrollTopRef.current !== 0) {
						setShowNewNotificationsButton(true);
					}
				},
			});
		}, [loadPrevious]),
	);

	const loadMore = React.useCallback(
		(callback: () => void) => {
			loadNext(20, {
				onComplete(error) {
					if (error) {
						Sentry.captureException(error);
					}
					// Call the callback even if there's an error so the loading state goes
					// away.
					callback();
				},
			});
		},
		[loadNext],
	);

	const [scrollContainer, setScrollContainer] = React.useState<HTMLDivElement | null>(null);
	const scrollContainerRef = React.useRef<HTMLDivElement>();

	const handleScrollContainerRef = React.useCallback((el: HTMLDivElement) => {
		setScrollContainer(el);
		scrollContainerRef.current = el;
	}, []);

	const editedNoteSquashInfo = React.useMemo(() => {
		return squashEditedNoteNotifications(data.notifications.edges.map(edge => edge.node));
	}, [data]);

	return (
		<div className={styles.notificationListContainerContainer}>
			<div ref={handleScrollContainerRef} className={styles.notificationListContainer} onScroll={onScroll}>
				{showNewNotificationsButton && (
					<Button size="normal" onClick={onNewNotificationsButtonClick} className={styles.newNotificationsButton}>
						<FontAwesomeIcon icon={faArrowUp} className={styles.newNotificationsButtonIcon} />
						New notifications
					</Button>
				)}
				{data.notifications.edges.length === 0 ? (
					<EmptyState />
				) : (
					<ul className={styles.notificationList}>
						{data.notifications.edges.map(({ node }) => (
							<NotificationCenterEntry
								key={node.id}
								notification={node}
								squashInfo={
									node.verb === "EDITED_NOTE" && node.outerObject
										? editedNoteSquashInfo?.[node.outerObject.id]
										: undefined
								}
							/>
						))}
					</ul>
				)}
				{hasNext && (
					<ScrollContext.Provider value={scrollContainer}>
						<LoadMoreButton dotted={false} fireOnMount={true} loadMore={loadMore} />
					</ScrollContext.Provider>
				)}
			</div>
		</div>
	);
}

function NotificationCenterPanel() {
	const fetchKey = useDailyUpdate();
	const [isFiltersOpen, setIsFiltersOpen] = React.useState(false);
	const [filtersMutationLoading, setFiltersMutationLoading] = React.useState(false);

	const data = useLazyLoadQuery<NotificationCenterQuery>(
		graphql`
			query NotificationCenterQuery {
				viewer {
					user {
						id
						isGuest
					}

					notificationCenter {
						id
						unreadNotificationCount
						otherNotificationCount
						notificationPanelFilters {
							key
							value
						}

						...NotificationCenter_List
					}
				}
			}
		`,
		{},
		{ fetchPolicy: "store-and-network", fetchKey },
	);

	const markAllAsRead = React.useCallback(async () => {
		// Block loading more stuff so that the result of the mutations update
		// every notification that the client has in the relay store.
		if (!(await markAllNotificationsAsRead())) {
			toast.error("Failed to mark all notifications as read");
		}
	}, []);

	const [enabledFilters, setEnabledFilters] = React.useState<NotificationFilterChoice[]>(
		data.viewer.notificationCenter?.notificationPanelFilters.filter(item => !!item.value).map(item => item.key) || [],
	);

	const syncEnabledFilters = React.useCallback(async (filters: NotificationFilterChoice[]) => {
		setFiltersMutationLoading(true);
		if (!(await setNotificationsFilters(filters))) {
			toast.error("Failed to set filters");
		}
		setFiltersMutationLoading(false);
	}, []);

	const debouncedSyncEnabledFilters = React.useMemo(() => {
		return debounce(async (filters: NotificationFilterChoice[]) => {
			await syncEnabledFilters(filters);
		}, 500);
	}, [syncEnabledFilters]);

	const onFilterItemSelected = React.useCallback(
		(item: NotificationFilterChoice) => () => {
			const isEnabled = enabledFilters.includes(item);
			setEnabledFilters(prev => {
				const filters = isEnabled ? prev.filter(key => key !== item) : [...prev, item];
				debouncedSyncEnabledFilters(filters);
				return filters;
			});
		},
		[enabledFilters, debouncedSyncEnabledFilters],
	);

	const onResetFiltersClicked = React.useCallback(
		(isDebounced: boolean) => () => {
			setEnabledFilters(prev => {
				const filters = Object.keys(notificationPanelLabels) as NotificationFilterChoice[];
				if (prev.length !== filters.length) {
					if (isDebounced) {
						debouncedSyncEnabledFilters(filters);
					} else {
						syncEnabledFilters(filters);
					}
				}
				return filters;
			});
		},
		[syncEnabledFilters, debouncedSyncEnabledFilters],
	);

	const { openSettingsModal } = useContext(AppContext);
	const openNotificationSettings = React.useCallback(() => {
		openSettingsModal("notifications");
	}, [openSettingsModal]);

	if (!data.viewer.notificationCenter) return null;

	const { notificationCenter } = data.viewer;
	const isAllFiltersEnabled = enabledFilters.length === Object.keys(notificationPanelLabels).length;

	const isGuestAccess = data.viewer.user.isGuest === true;

	return (
		<section className={styles.panel}>
			<header className={styles.header}>
				<div className={styles.headerRow}>
					<h1>Notifications</h1>
					<Menu>
						<Menu.Trigger>
							<IconButton className={styles.kebabMenuTrigger} icon={faEllipsisV} title="More actions" />
						</Menu.Trigger>
						<Menu.Content>
							<Menu.Item onSelect={openNotificationSettings}>Notification settings</Menu.Item>
						</Menu.Content>
					</Menu>
				</div>

				<div className={styles.headerRow}>
					<Menu open={isFiltersOpen} onOpenChange={setIsFiltersOpen}>
						<Menu.Trigger>
							<div className={cx(styles.filtersButton, { [styles.open]: isFiltersOpen })}>
								<div>Filters</div>
								<FontAwesomeIcon icon={isFiltersOpen ? faCaretUp : faCaretDown} />
							</div>
						</Menu.Trigger>
						<Menu.Content className={styles.filterMenuContent}>
							{notificationCenter.notificationPanelFilters
								.filter(
									item =>
										// Guest users can only see certain types of notifications
										!isGuestAccess || (isGuestAccess && guestOnlyNotificationTypes.includes(item.key)),
								)
								.map(item => {
									return (
										<Menu.CheckboxItem
											key={item.key}
											checked={enabledFilters.includes(item.key)}
											onCheckedChange={onFilterItemSelected(item.key)}
										>
											{notificationPanelLabels[item.key]}
										</Menu.CheckboxItem>
									);
								})}
							<Menu.Separator />
							<Menu.Item
								onSelect={onResetFiltersClicked(true)}
								disabled={isAllFiltersEnabled}
								className={cx(styles.resetFiltersLabel, { [styles.disabled]: isAllFiltersEnabled })}
							>
								Reset all filters
							</Menu.Item>
						</Menu.Content>
					</Menu>

					{notificationCenter.unreadNotificationCount > 0 && (
						<p onClick={markAllAsRead} className={styles.markAllAsRead}>
							Mark all as read
						</p>
					)}
				</div>
			</header>
			{!isAllFiltersEnabled && notificationCenter.otherNotificationCount > 0 && (
				<div className={styles.otherNotifications} onClick={onResetFiltersClicked(false)}>
					{notificationCenter.otherNotificationCount > 9 ? "9+" : notificationCenter.otherNotificationCount} other
					{notificationCenter.otherNotificationCount === 1 ? " notification" : " notifications"} - Reset filters
				</div>
			)}
			{filtersMutationLoading ? (
				<LoadingSpinner dimmer={false} />
			) : (
				<NotificationCenterList userId={data.viewer.user.id} notificationCenter={notificationCenter} />
			)}
		</section>
	);
}

export default React.memo(withFallbacks(<LoadingSpinner dimmer={false} />, null, NotificationCenterPanel));
